/**
 * The report, rendered.
 *
 * ## The three rules this file exists to hold
 *
 * **1. Fail-closed, visibly.** The verdict is unsafe if any page failed *or* any page needs review.
 * `report.ts` recomputes it rather than trusting the payload; this says WHY, so an unsafe verdict
 * caused only by uncertainty does not look like a bug.
 *
 * **2. `not_applicable` renders NEUTRAL.** Under a blocks-only rule a body page requires nothing —
 * it is neither safe nor unsafe. Colouring it amber would recreate the exact noise the four-state
 * design was built to remove: the first real run produced *"Jane Smith: missing, John Doe: missing"*
 * on two perfectly fine body pages, which is how a reviewer talks themselves into a problem that
 * does not exist.
 *
 * **3. Matching is not forensics.** A confidence score is a vision model's similarity judgement,
 * useful for deciding which page to look at first. No label here may imply handwriting analysis,
 * and the caveat is on screen rather than in a footnote nobody reads.
 */

import { STATUS_COPY, ruleTitle, type PageObservation, type Report } from './report.js'

export function Dashboard({ report }: { report: Report }) {
  const dropped = report.pages_analyzed !== report.pages_expected

  return (
    <div className="report" data-safe={report.document_safe}>
      <Verdict report={report} />

      <div className="tiles">
        <Tile
          label="Pages checked"
          value={`${report.pages_analyzed}`}
          /* ⚠️ A mismatch is itself a finding. The workflow treats a dropped page as a reason to
             review, because a smaller number reported confidently makes a document look safer. */
          note={dropped ? `of ${report.pages_expected} expected` : undefined}
          tone={dropped ? 'warn' : 'plain'}
        />
        <Tile
          label="Problems"
          value={`${report.pages_flagged.length}`}
          note={report.pages_flagged.length > 0 ? `page ${report.pages_flagged.join(', ')}` : undefined}
          tone={report.pages_flagged.length > 0 ? 'bad' : 'plain'}
        />
        <Tile
          label="Need review"
          value={`${report.pages_needing_review.length}`}
          note={report.pages_needing_review.length > 0 ? `page ${report.pages_needing_review.join(', ')}` : undefined}
          tone={report.pages_needing_review.length > 0 ? 'warn' : 'plain'}
        />
        <Tile
          label="Identity matching"
          value={report.match_performed ? 'On' : 'Off'}
          /* ⚠️ `match_performed` is what separates "no samples were supplied" from "matched, and
             matched badly". Without it a reader cannot tell an absent capability from a poor result. */
          note={report.match_performed ? 'samples supplied' : 'no samples supplied'}
          tone="plain"
        />
      </div>

      <p className="report__rule">
        Checked against: <strong>{ruleTitle(report.signing_rule)}</strong>
        {report.parties.length > 0 && <> · Parties: {report.parties.join(', ')}</>}
      </p>

      <ol className="pages">
        {report.page_observations.map((page) => (
          <PageCard key={page.page_number} page={page} matching={report.match_performed} />
        ))}
      </ol>

      {report.match_performed && (
        <p className="report__caveat">
          ⚠️ Confidence scores come from a vision model comparing marks to the samples supplied. They
          are a guide to which page to look at first — not handwriting analysis, and not evidence.
        </p>
      )}
    </div>
  )
}

function Verdict({ report }: { report: Report }) {
  const failures = report.pages_flagged.length
  const reviews = report.pages_needing_review.length

  if (report.document_safe) {
    return (
      <div className="verdict verdict--safe">
        <span className="verdict__mark" aria-hidden="true">
          ✓
        </span>
        <div>
          <h2 className="verdict__headline">Signed as expected</h2>
          <p className="verdict__detail">
            Every page carries the marks the signing rule asks for, across {report.pages_analyzed} page
            {report.pages_analyzed === 1 ? '' : 's'}.
          </p>
        </div>
      </div>
    )
  }

  /*
   * ⚠️ The reason is spelled out because "unsafe" and "unsafe only because two pages were unclear"
   * are very different conversations, and the four-state design exists so a reader can tell them
   * apart. A single red banner would throw that away at the last step.
   */
  return (
    <div className="verdict verdict--unsafe">
      <span className="verdict__mark" aria-hidden="true">
        !
      </span>
      <div>
        <h2 className="verdict__headline">Not signed as expected</h2>
        <p className="verdict__detail">
          {failures > 0 && (
            <>
              {failures} page{failures === 1 ? '' : 's'} did not carry the expected marks
            </>
          )}
          {failures > 0 && reviews > 0 && ', and '}
          {reviews > 0 && (
            <>
              {reviews} page{reviews === 1 ? '' : 's'} could not be read confidently
            </>
          )}
          {failures === 0 && reviews === 0 && 'the report did not confirm the document as signed'}.
          {reviews > 0 && failures === 0 && (
            <> Uncertainty blocks a pass on purpose — an unclear page is not a signed one.</>
          )}
        </p>
      </div>
    </div>
  )
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: string
  note?: string | undefined
  tone: 'plain' | 'warn' | 'bad'
}) {
  return (
    <div className={`tile tile--${tone}`}>
      <span className="tile__value">{value}</span>
      <span className="tile__label">{label}</span>
      {note !== undefined && <span className="tile__note">{note}</span>}
    </div>
  )
}

function PageCard({ page, matching }: { page: PageObservation; matching: boolean }) {
  const copy = STATUS_COPY[page.status]
  return (
    <li className={`page page--${page.status}`} data-page={page.page_number} data-status={page.status}>
      <div className="page__head">
        <span className="page__number">Page {page.page_number}</span>
        <span className="page__status">{copy.label}</span>
        <span className="page__kind">
          {page.page_kind === 'signature_block'
            ? 'signature block'
            : page.page_kind === 'body'
              ? 'body page'
              : 'kind unclear'}
        </span>
      </div>

      <p className="page__expected">
        {copy.detail}
        {page.expected_marks !== '' && page.expected_marks !== 'none' && (
          <>
            {' '}
            Expected: {page.expected_marks}. Found {page.marks_found} mark
            {page.marks_found === 1 ? '' : 's'}.
          </>
        )}
      </p>

      {/* ⚠️ Empty on a `not_applicable` page, and that is enforced upstream too — listing every
          party as "missing" on a page that required nothing is noise shaped exactly like a finding. */}
      {page.party_results.length > 0 && (
        <ul className="parties">
          {page.party_results.map((party) => (
            <li key={party.name} className={`party-chip party-chip--${party.status}`}>
              <span className="party-chip__name">{party.name}</span>
              <span className="party-chip__status">{partyLabel(party.status)}</span>
              {matching && party.confidence > 0 && (
                <span className="party-chip__score" title="Visual similarity to the supplied sample — a triage aid">
                  {party.confidence}%
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {page.reasoning !== '' && <p className="page__reasoning">{page.reasoning}</p>}
    </li>
  )
}

/**
 * ⚠️ `present_unverified` is a real and important state: a mark IS there, but nothing was supplied
 * to check it against. Rendering it as "matched" would claim a verification that never happened;
 * rendering it as "missing" would report a problem that does not exist.
 */
function partyLabel(status: string): string {
  switch (status) {
    case 'matched':
      return 'matched a sample'
    case 'present_unverified':
      return 'signed — not checked against a sample'
    case 'missing':
      return 'no mark found'
    default:
      return status.replace(/_/g, ' ')
  }
}
